import { useOffline } from '../contexts/OfflineContext';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Calendar,
  Check,
  CheckCircle2,
  Clock,
  ListTodo,
  MapPin,
  MessageCircle,
  SearchX,
  SlidersHorizontal,
  User,
  Users,
  XCircle,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import Dropdown from '../components/Dropdown';
import ModalShell from '../components/ModalShell';
import { API_URL } from '../config';
import useToast from '../hooks/useToast';
import { getTasks, takeTask } from '../services/tasksApi';
import { STAGE_TAB_STYLES } from '../utils/stages';
import { safeFetch } from '../utils/safeFetch';
import { formatTimezoneDate } from '../utils/timezone';
import { haptic } from '../utils/vibration';
import { readOfflineTasks, readOfflineRoutesSnapshot, shouldUseOfflineDetailFallback } from '../utils/offlineRoutesCache';

const PRIVILEGED_TYPES = new Set(['dispatcher', 'admin', 'developer']);
const TASKS_PAGE_SIZE = 20;

const EMPTY_STATE = {
  my: {
    title: 'У вас нет поручений',
    subtitle: 'Возьмите поручение из вкладки "Доступные".',
  },
  available: {
    title: 'Нет доступных поручений',
    subtitle: 'Все поручения уже распределены или ещё не созданы.',
  },
  archive: {
    title: 'Архив пуст',
    subtitle: 'Здесь появятся выполненные вами поручения.',
  },
  all: {
    title: 'Поручений нет',
    subtitle: 'Попробуйте сменить фильтр или дождитесь новых поручений.',
  },
};

const buildTabItems = ({ isPrivileged, worksWithTasks }) => {
  const result = [];
  if (worksWithTasks) {
    result.push({ id: 'my', label: 'Мои' });
  }
  result.push({ id: 'available', label: 'Доступные' });
  result.push({ id: 'archive', label: 'Архив' });
  if (isPrivileged) {
    result.push({ id: 'all', label: 'Все' });
  }
  return result;
};

const ALL_STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Все' },
  { value: 'available', label: 'Доступные' },
  { value: 'assigned', label: 'Назначенные' },
  { value: 'completed', label: 'Выполненные' },
  { value: 'cancelled', label: 'Отменённые' },
];

const formatTimeSlot = (timeSlot = '') => String(timeSlot || '').trim().replace(/\s*-\s*/g, ' - ');

const shortenDisplayAddress = (address = '') => {
  const value = String(address || '').trim();
  const parts = value.split(',').map((part) => part.trim()).filter(Boolean);
  const cityIndex = parts.findIndex((part) => /^г\.?\s/i.test(part));
  if (cityIndex > 0 && parts.slice(0, cityIndex).some((part) => /обл\.?|область/i.test(part))) {
    return parts.slice(cityIndex).join(', ');
  }
  return value;
};

const getTaskStatusBadge = (task) => {
  if (task.status === 'cancelled') {
    return {
      label: 'Отменено',
      orderBadgeClass: 'bg-red-100 text-red-700 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-900/40',
    };
  }

  if (task.status === 'completed') {
    return {
      label: 'Выполнено',
      orderBadgeClass: 'bg-green-100 text-green-700 ring-green-200 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-900/40',
    };
  }

  if (task.status === 'available') {
    return {
      label: 'Доступно',
      orderBadgeClass: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-900/30 dark:text-slate-300 dark:ring-slate-800',
    };
  }

  return {
    label: 'Назначено',
    orderBadgeClass: 'bg-blue-100 text-blue-700 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-900/40',
  };
};

const getTaskTimeStartMinutes = (timeSlot = '') => {
  const match = String(timeSlot).match(/(\d{1,2})[:.](\d{2})/);
  if (!match) return 24 * 60;
  return Number(match[1]) * 60 + Number(match[2]);
};

const getTaskOverdueMeta = (task) => {
  const dateKey = task?.scheduled_date_key || String(task?.scheduled_date || '').slice(0, 10);
  const [year, month, day] = String(dateKey).split('-').map(Number);
  if (!year || !month || !day) {
    return { dateOverdue: false, timeOverdue: false };
  }

  const taskDay = new Date(year, month - 1, day);
  if (Number.isNaN(taskDay.getTime())) {
    return { dateOverdue: false, timeOverdue: false };
  }

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const taskDayStart = new Date(taskDay.getFullYear(), taskDay.getMonth(), taskDay.getDate());
  const dateOverdue = taskDayStart.getTime() < todayStart.getTime();
  const isSameDay = taskDayStart.getTime() === todayStart.getTime();
  const timeOverdue = isSameDay && !!task?.time_slot && getTaskTimeStartMinutes(task.time_slot) < (now.getHours() * 60 + now.getMinutes());

  return { dateOverdue, timeOverdue };
};

const TaskListSkeleton = ({ count = 4 }) => (
  <div className="space-y-2">
    {[...Array(count)].map((_, index) => (
      <div
        key={`tasks-skeleton-${index}`}
        className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900"
      >
        <div className="space-y-3 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-5 w-24 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="h-4 w-1/2 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
            </div>
            <div className="h-4 w-24 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
          </div>
          <div className="h-px bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-3/4 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
          <div className="h-4 w-full animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
          <div className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>
    ))}
  </div>
);

const TaskCard = ({ task, activeTab, onOpen, onTake, taking = false }) => {
  const badge = getTaskStatusBadge(task);
  const shouldHighlightOverdue = ['my', 'available', 'all'].includes(activeTab) && !['completed', 'cancelled'].includes(task.status);
  const overdueMeta = getTaskOverdueMeta(task);
  const dateText = task.scheduled_date ? formatTimezoneDate(task.scheduled_date, false) : 'Дата не указана';
  const timeText = formatTimeSlot(task.time_slot) || 'Время не указано';
  const assigneeName = String(task.assigned_to_name || '').trim() || 'Не назначено';
  const creatorName = String(task.created_by_name || '').trim() || `Пользователь #${task.created_by}`;
  const AssigneeIcon = task.assigned_to_name ? User : Users;
  const ArchiveIcon = task.status === 'cancelled' ? XCircle : CheckCircle2;
  const archiveIconClassName = task.status === 'cancelled'
    ? 'text-red-600 dark:text-red-400'
    : 'text-green-600 dark:text-green-400';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:border-gray-300 active:scale-[0.99] dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600"
    >
      <div className="px-4 pb-3 pt-2">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1">
          <div className="flex min-w-0 items-center gap-2">
            <span className={`inline-flex h-5 items-center rounded-md px-1.5 text-[12px] font-medium leading-none ring-1 ${badge.orderBadgeClass}`}>
              № {task.task_number}
            </span>
            {activeTab === 'archive' ? (
              <span
                className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-gray-100 text-[11px] font-semibold ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700"
                title={badge.label}
                aria-label={badge.label}
              >
                <ArchiveIcon className={`h-3 w-3 ${archiveIconClassName}`} />
              </span>
            ) : null}
          </div>

          <div className={`flex h-5 items-center justify-end gap-1.5 text-right text-[12px] font-medium leading-4 ${
            shouldHighlightOverdue && overdueMeta.dateOverdue ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'
          }`}>
            <Calendar className={`h-3 w-3 shrink-0 ${
              shouldHighlightOverdue && overdueMeta.dateOverdue ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'
            }`} />
            <span>{dateText}</span>
          </div>

          <div className="flex min-h-4 min-w-0 items-center gap-1.5 text-[11px] leading-4 text-gray-600 dark:text-gray-300">
            <AssigneeIcon className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
            <span className="min-w-0 truncate">{assigneeName}</span>
          </div>

          <div className={`flex min-h-4 items-center justify-end gap-1.5 text-right text-[11px] font-medium leading-4 ${
            shouldHighlightOverdue && overdueMeta.timeOverdue ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'
          }`}>
            <Clock className={`h-3 w-3 shrink-0 ${
              shouldHighlightOverdue && overdueMeta.timeOverdue ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'
            }`} />
            <span>{timeText}</span>
          </div>
        </div>

        <div className="mt-2 border-t border-gray-200 dark:border-gray-700" />

          <div className="mt-3 flex min-h-6 items-start justify-between gap-3">
          <div className="mt-3 flex min-w-0 flex-1 items-center gap-1.5 text-[11px] leading-4 text-gray-600 dark:text-gray-300">
            <ListTodo className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
            <span className="min-w-0 truncate">Создал: {creatorName}</span>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {task.unread_comments_count > 0 ? (
              <span
                className="inline-flex h-6 min-w-6 items-center justify-center gap-1 rounded-lg bg-gray-100 px-1.5 text-[11px] font-semibold text-gray-700 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700"
                title="Непрочитанные комментарии"
                aria-label={`Непрочитанные комментарии: ${task.unread_comments_count}`}
              >
                <MessageCircle className="h-3 w-3 text-red-500 dark:text-red-400" />
                {task.unread_comments_count || ''}
              </span>
            ) : null}
          </div>
        </div>

        <div className="mt-1 flex items-start gap-1 text-[12px] font-medium leading-4 text-gray-950 dark:text-gray-50">
          <MapPin className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
          <span className="break-words">{shortenDisplayAddress(task.address_text) || 'Адрес не указан'}</span>
        </div>
      </div>

      {activeTab === 'available' && task.can_take ? (
        <div className="px-4 pb-3 pt-0">
          <button
            onClick={(event) => {
              event.stopPropagation();
              onTake?.();
            }}
            disabled={taking}
            className="route-order-action-button inline-flex h-[34px] min-h-[34px] w-full items-center justify-center gap-1.5 rounded-lg border border-green-500 bg-transparent px-3 text-xs font-semibold leading-none text-green-700 transition-colors hover:bg-green-50 disabled:opacity-50 dark:text-green-300 dark:hover:bg-green-900/20"
          >
            <Check className="h-3 w-3" />
            <span>{taking ? 'Выполняю...' : 'Взять в работу'}</span>
          </button>
        </div>
      ) : null}
    </div>
  );
};

const TasksStagePage = () => {
  const offline = useOffline();
  const connectionUnavailable = ['offline', 'server_unavailable'].includes(offline?.networkStatus);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast(3200);

  const [auth, setAuth] = useState({
    loaded: false,
    userType: localStorage.getItem('user_type') || 'user',
    worksWithTasks: false,
  });
  const [tasks, setTasks] = useState([]);
  const [usingOfflineTasks, setUsingOfflineTasks] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [takingTaskId, setTakingTaskId] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [allStatusFilter, setAllStatusFilter] = useState('all');
  const loadMoreSentinelRef = useRef(null);
  const loadedQueryKeyRef = useRef(null);

  const isPrivileged = PRIVILEGED_TYPES.has(String(auth.userType || '').toLowerCase());

  const tabs = useMemo(
    () => buildTabItems({ isPrivileged, worksWithTasks: auth.worksWithTasks }),
    [auth.worksWithTasks, isPrivileged]
  );

  const requestedTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('tab') || '').trim().toLowerCase();
  }, [location.search]);

  const activeTab = tabs.some((tab) => tab.id === requestedTab) ? requestedTab : (tabs[0]?.id || 'available');
  const queryKey = `${activeTab}:${allStatusFilter}`;
  const emptyState = EMPTY_STATE[activeTab] || EMPTY_STATE.available;

  useEffect(() => {
    if (error) {
      showToast(error, 'error');
    }
  }, [error, showToast]);

  useEffect(() => {
    let cancelled = false;

    const loadAuth = async () => {
      try {
        if (connectionUnavailable || navigator.onLine === false) throw new Error('Нет соединения с сервером');
        const data = await safeFetch(`${API_URL}/api/check-auth`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (cancelled) return;

        setAuth({
          loaded: true,
          userType: data.user_type || 'user',
          worksWithTasks: Boolean(data.works_with_tasks),
        });
      } catch (fetchError) {
        if (cancelled) return;
        if (shouldUseOfflineDetailFallback(fetchError)) {
          const saved = await readOfflineRoutesSnapshot().catch(() => null);
          if (cancelled) return;
          if (saved) {
            setAuth({ loaded: true, userType: saved.userType || localStorage.getItem('user_type') || 'user', worksWithTasks: saved.worksWithTasks });
            return;
          }
        }
        setAuth((prev) => ({ ...prev, loaded: true }));
        setError(fetchError.message || 'Не удалось загрузить доступы');
      }
    };

    loadAuth();
    return () => {
      cancelled = true;
    };
  }, [connectionUnavailable]);

  useEffect(() => {
    if (!auth.loaded || !tabs.length) return;
    if (requestedTab && tabs.some((tab) => tab.id === requestedTab)) return;
    navigate(`/tasks?tab=${tabs[0].id}`, { replace: true });
  }, [auth.loaded, navigate, requestedTab, tabs]);

  useEffect(() => {
    loadedQueryKeyRef.current = null;
    setOffset(0);
    setTasks([]);
    setHasMore(false);
    setLoadingMore(false);
    setShowAllFilters(false);
  }, [queryKey]);

  useEffect(() => {
    if (!auth.loaded) return;

    let cancelled = false;

    const loadTasks = async () => {
      const nextOffset = 0;
      let hasCached = false;
      setError('');

      // Сначала пытаемся мгновенно применить локальный офлайн-кэш поручений (без скелетона)
      try {
        const saved = await readOfflineTasks(activeTab);
        if (cancelled) return;
        if (Array.isArray(saved) && saved.length > 0) {
          setTasks(saved);
          setOffset(saved.length);
          setHasMore(false);
          setUsingOfflineTasks(true);
          hasCached = true;
        }
      } catch (cacheErr) {
        // ignore
      }

      if (!hasCached) {
        setLoading(true);
      }

      try {
        if (connectionUnavailable || navigator.onLine === false) throw new Error('Нет соединения с сервером');
        const params = {
          scope: activeTab,
          limit: TASKS_PAGE_SIZE,
          offset: nextOffset,
        };
        if (activeTab === 'all' && allStatusFilter !== 'all') {
          params.status_filter = allStatusFilter;
        }
        const data = await getTasks(params);
        if (!cancelled) {
          const nextItems = Array.isArray(data) ? data : [];
          loadedQueryKeyRef.current = queryKey;
          setTasks(nextItems);
          setOffset(nextItems.length);
          setHasMore(nextItems.length === TASKS_PAGE_SIZE);
          setUsingOfflineTasks(false);
        }
      } catch (fetchError) {
        if (cancelled) return;
        if (!hasCached) {
          if (shouldUseOfflineDetailFallback(fetchError)) {
            const saved = await readOfflineTasks(activeTab).catch(() => null);
            if (cancelled) return;
            if (saved) {
              setTasks(saved);
              setOffset(saved.length);
              setHasMore(false);
              setUsingOfflineTasks(true);
              return;
            }
          }
          setTasks([]);
          setOffset(0);
          setHasMore(false);
          setError(fetchError.message || 'Не удалось загрузить поручения');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTasks();
    return () => {
      cancelled = true;
    };
  }, [activeTab, allStatusFilter, auth.loaded, queryKey, connectionUnavailable]);

  const handleLoadMore = useCallback(async () => {
    if (connectionUnavailable || usingOfflineTasks || loadingMore || !hasMore || loadedQueryKeyRef.current !== queryKey) return;
    setLoadingMore(true);
    try {
      const params = {
        scope: activeTab,
        limit: TASKS_PAGE_SIZE,
        offset,
      };
      if (activeTab === 'all' && allStatusFilter !== 'all') {
        params.status_filter = allStatusFilter;
      }
      const data = await getTasks(params);
      if (loadedQueryKeyRef.current !== queryKey) return;
      const nextItems = Array.isArray(data) ? data : [];
      setTasks((prev) => [...prev, ...nextItems]);
      setOffset((prev) => prev + nextItems.length);
      setHasMore(nextItems.length === TASKS_PAGE_SIZE);
    } catch (fetchError) {
      setError(fetchError.message || 'Не удалось загрузить ещё поручения');
    } finally {
      setLoadingMore(false);
    }
  }, [activeTab, allStatusFilter, hasMore, loadingMore, offset, queryKey, connectionUnavailable, usingOfflineTasks]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMore || loading || loadingMore || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      { rootMargin: '320px 0px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleLoadMore, hasMore, loading, loadingMore]);

  const handleTakeTask = async (taskId) => {
    if (usingOfflineTasks || connectionUnavailable || navigator.onLine === false) {
      showToast('Сохранённые поручения доступны только для просмотра.', 'info');
      return;
    }
    if (!taskId) return;
    setTakingTaskId(taskId);
    try {
      const updatedTask = await takeTask(taskId);
      setTasks((prev) => prev.filter((task) => task.id !== taskId));
      showToast(`Поручение №${updatedTask?.task_number || taskId} назначено на вас`, 'success');
    } catch (requestError) {
      showToast(requestError.message || 'Не удалось взять поручение', 'error');
    } finally {
      setTakingTaskId(null);
      setConfirmModal(null);
    }
  };

  return (
    <div className="space-y-3 desktop-content-container-wide">
      <div className="overflow-hidden rounded-xl bg-white shadow-sm dark:bg-gray-900">
        <div
          className="grid border-b border-gray-200 dark:border-gray-700"
          style={{ gridTemplateColumns: `repeat(${Math.max(tabs.length, 1)}, minmax(0, 1fr))` }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                haptic('selection');
                navigate(`/tasks?tab=${tab.id}`, { replace: true });
              }}
              className={`border-b-2 px-2 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? (STAGE_TAB_STYLES.blue || 'border-blue-500 text-blue-600 dark:text-blue-400')
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {auth.loaded && !auth.worksWithTasks ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/25 dark:text-amber-200">
          Рабочий режим поручений у этого пользователя выключен. Раздел доступен только для просмотра и диспетчерских действий.
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        {activeTab === 'all' ? (
          <button
            type="button"
            onClick={() => {
              haptic('selection');
              setShowAllFilters((prev) => !prev);
            }}
            className="inline-flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Фильтр
          </button>
        ) : null}
        {isPrivileged && activeTab === 'all' ? (
          <button
            type="button"
            onClick={() => {
              haptic('open');
              navigate(`/tasks/new?tab=${activeTab}`);
            }}
            className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
          >
            <ListTodo className="h-4 w-4" />
            Новое поручение
          </button>
        ) : null}
      </div>

      {activeTab === 'all' && showAllFilters ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <Dropdown
            label="Статус"
            value={allStatusFilter}
            options={ALL_STATUS_FILTER_OPTIONS}
            onChange={(value) => {
              haptic('selection');
              setAllStatusFilter(String(value));
            }}
            placeholder="Выберите статус"
            menuLabel="Статусы поручений"
          />
        </div>
      ) : null}

      <div className="space-y-2">
        {loading ? (
          <TaskListSkeleton count={activeTab === 'available' ? 4 : 3} />
        ) : tasks.length === 0 ? (
          <div className="rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-gray-900">
            <SearchX className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-gray-100">{emptyState.title}</h3>
            <p className="text-gray-600 dark:text-gray-400">{emptyState.subtitle}</p>
          </div>
        ) : (
          <>
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                activeTab={activeTab}
                taking={takingTaskId === task.id}
                onTake={() => {
                  haptic('tap');
                  setConfirmModal({ taskId: task.id, taskNumber: task.task_number });
                }}
                onOpen={() => {
                  haptic('tap');
                  navigate(`/tasks/${task.id}?from=tasks&tab=${activeTab}`);
                }}
              />
            ))}
            {hasMore ? (
              <div ref={loadMoreSentinelRef} aria-live="polite">
                {loadingMore ? <TaskListSkeleton count={1} /> : <div className="h-1" />}
              </div>
            ) : null}
          </>
        )}
      </div>

      

      <ModalShell
        open={Boolean(confirmModal)}
        onClose={() => setConfirmModal(null)}
        variant="sheet"
        panelClassName="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl dark:bg-gray-900"
      >
        {confirmModal && (
          <div>
            <div className="mb-3 flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-300">
                <Check className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Взять поручение в работу?</h3>
                <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
                  Поручение №{confirmModal.taskNumber}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  haptic('close');
                  setConfirmModal(null);
                }}
                disabled={takingTaskId === confirmModal.taskId}
                className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => {
                  haptic('success');
                  handleTakeTask(confirmModal.taskId);
                }}
                disabled={takingTaskId === confirmModal.taskId}
                className="min-h-[40px] flex-1 rounded-lg bg-green-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-green-700 disabled:opacity-50"
              >
                {takingTaskId === confirmModal.taskId ? 'Выполняю...' : 'Взять в работу'}
              </button>
            </div>
          </div>
        )}
      </ModalShell>
    </div>
  );
};

export default TasksStagePage;
